import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box} from '@mui/material';

/**
 * Wraps a <video> with a wall-clock UTC overlay synced to currentTime.
 * Zero server cost — overlay is positioned HTML updated via requestAnimationFrame
 * only while the video is playing (cancelled on pause/seek-finished).
 *
 * Props:
 *   src         — signed URL of the mp4
 *   thumbnail   — optional poster (signed URL of the jpg)
 *   recordedAt  — ISO-8601 UTC timestamp of the first frame (from blob `dt`
 *                 metadata, surfaced by /api/games/{id}/video). If absent,
 *                 the overlay is not rendered — video still works.
 */
const VideoWithTimestamp = ({src, thumbnail, recordedAt}) => {
    const videoRef = useRef(null);
    const rafRef = useRef(null);
    const [overlay, setOverlay] = useState('');

    const baseMs = useMemo(
        () => recordedAt ? new Date(recordedAt).getTime() : null,
        [recordedAt]
    );

    const formatWall = useCallback((currentTime) => {
        if (baseMs == null) return '';
        const wallMs = baseMs + currentTime * 1000;
        // ISO `2026-05-25T01:44:42.193Z` → `2026-05-25 01:44:42.193 UTC`
        return new Date(wallMs).toISOString().replace('T', ' ').replace('Z', ' UTC');
    }, [baseMs]);

    const tick = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        setOverlay(formatWall(v.currentTime));
        rafRef.current = requestAnimationFrame(tick);
    }, [formatWall]);

    const stop = useCallback(() => {
        if (rafRef.current != null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);

    const start = useCallback(() => {
        if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick);
    }, [tick]);

    // Also update on scrub / metadata-load so overlay is correct while paused.
    const snap = useCallback(() => {
        const v = videoRef.current;
        if (v) setOverlay(formatWall(v.currentTime));
    }, [formatWall]);

    useEffect(() => stop, [stop]);

    return (
        <Box sx={{position: 'relative'}}>
            <video
                ref={videoRef}
                controls
                preload="metadata"
                poster={thumbnail}
                onPlay={start}
                onPause={stop}
                onEnded={stop}
                onSeeked={snap}
                onLoadedMetadata={snap}
                style={{width: '100%', borderRadius: '4px', display: 'block'}}>
                <source src={src} type="video/mp4"/>
                Your browser does not support the video tag.
            </video>
            {baseMs != null && (
                <Box sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.65)',
                    color: 'common.white',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    pointerEvents: 'none',  // clicks pass through to <video> controls
                }}>
                    {overlay}
                </Box>
            )}
        </Box>
    );
};

export default VideoWithTimestamp;
